/**
 * DeviceLogger - Almacena logs de diagnóstico en IndexedDB.
 * Eventos: reproducción, conectividad, errores, console.warn/error.
 * Límite: 5,000 registros FIFO.
 */
/* global Capacitor */
import { openDB } from 'idb';

const DB_NAME = 'vybrooDB';
const STORE_NAME = 'device_logs';
const MAX_LOG_ENTRIES = 5000;

class DeviceLogger {
  constructor() {
    this.db = null;
    this._origConsoleWarn = null;
    this._origConsoleError = null;
    this._capturing = false;
  }

  async init() {
    if (this.db) return;
    this.db = await openDB(DB_NAME, 3, {
      upgrade(db, _oldVersion, _newVersion, _transaction) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
          store.createIndex('event', 'event', { unique: false });
          store.createIndex('ts', 'ts', { unique: false });
          store.createIndex('device_id', 'device_id', { unique: false });
        }
      },
    });
  }

  async log(event, data = {}, extra = {}) {
    try {
      await this.init();
      await this.db.add(STORE_NAME, {
        ts: new Date().toISOString(),
        event,
        data,
        'device_id': extra.device_id || '',
        'session_id': extra.session_id || '',
        version: extra.version || '',
        online: typeof navigator !== 'undefined' ? navigator.onLine : null,
        'app_mode': extra.app_mode || this._detectAppMode(),
      });
      await this._trimToLimit();
    } catch (e) {
      console.debug('DeviceLogger.log error:', e);
    }
  }

  async getAll() {
    await this.init();
    const items = await this.db.getAll(STORE_NAME);
    return items.sort((a, b) => new Date(b.ts) - new Date(a.ts));
  }

  async count() {
    await this.init();
    return this.db.count(STORE_NAME);
  }

  async clear() {
    await this.init();
    await this.db.clear(STORE_NAME);
  }

  async export(limit = MAX_LOG_ENTRIES) {
    await this.init();
    const all = await this.db.getAll(STORE_NAME);
    return all
      .sort((a, b) => new Date(b.ts) - new Date(a.ts))
      .slice(0, limit)
      .map((e) => ({
        ts: e.ts,
        event: e.event,
        data: e.data,
        online: e.online,
        'app_mode': e.app_mode,
      }));
  }

  captureConsole() {
    if (this._capturing) return;
    this._capturing = true;
    this._origConsoleWarn = console.warn;
    console.warn = (...args) => {
      this.log('console_warn', { message: args.map((a) => (typeof a === 'object' ? JSON.stringify(a) : String(a))).join(' ') });
      if (this._origConsoleWarn) this._origConsoleWarn.apply(console, args);
    };
    this._origConsoleError = console.error;
    console.error = (...args) => {
      this.log('console_error', { message: args.map((a) => (typeof a === 'object' ? JSON.stringify(a) : String(a))).join(' ') });
      if (this._origConsoleError) this._origConsoleError.apply(console, args);
    };
  }

  restoreConsole() {
    if (!this._capturing) return;
    this._capturing = false;
    if (this._origConsoleWarn) { console.warn = this._origConsoleWarn; this._origConsoleWarn = null; }
    if (this._origConsoleError) { console.error = this._origConsoleError; this._origConsoleError = null; }
  }

  _detectAppMode() {
    if (typeof window === 'undefined') return 'unknown';
    if (window.matchMedia('(display-mode: standalone)').matches) return 'pwa';
    if (typeof Capacitor !== 'undefined' && Capacitor.getPlatform() !== 'web') return 'capacitor';
    return 'browser';
  }

  async _trimToLimit() {
    try {
      const count = await this.db.count(STORE_NAME);
      if (count <= MAX_LOG_ENTRIES) return;
      const all = await this.db.getAll(STORE_NAME);
      const sorted = all.sort((a, b) => a.id - b.id);
      const toDelete = sorted.slice(0, sorted.length - MAX_LOG_ENTRIES);
      const tx = this.db.transaction(STORE_NAME, 'readwrite');
      for (const entry of toDelete) tx.store.delete(entry.id);
      await tx.done;
    } catch (e) { console.debug('DeviceLogger._trimToLimit error:', e); }
  }
}

const deviceLogger = new DeviceLogger();
export default deviceLogger;
